import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Box, Container, Snackbar } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ConfirmModal } from 'components/Modal';
import {
  getMentor,
  updateMentor,
  activateMentor,
  deactivateMentor,
} from 'apis';

import { useAuth } from 'app/ProviderAuth';
import { Button } from 'components';
import { MentorForm } from './components/MentorForm';
import { Mentor } from 'types';
import { PauseIcon } from 'resource';
import { defaultWeeklySchedule } from 'helper/time';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

export const EditMentorProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isMentor } = useAuth();
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [userDocID, setUserDocID] = useState('');
  const [msgBoxOpen, setMsgBoxOpen] = useState(false);
  const [mentorActive, setMentorActive] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
  } = useForm<Mentor>({
    defaultValues: {
      name: '',
      email: user?.email ?? '',
      pictureUrl: '',
      profileBio: '',
      title: '',
      tags: [],
      availableTime: defaultWeeklySchedule,
      bookingLink: '',
      timezone: '',
      countryCode: '',
      uid: '',
    },
    mode: 'onBlur',
  });
  useEffect(() => {
    if (user) {
      const fetchData = async function () {
        const mentorSnapshot = await getMentor(user.uid);
        if (!mentorSnapshot.exists) {
          navigate('/door');
          return;
        }
        const profile = mentorSnapshot.data();
        setMentorActive(profile!.isActivated ?? true);
        setMsgBoxOpen(!(profile!.isActivated ?? true));

        const {
          name,
          pictureUrl,
          profileBio,
          email,
          title,
          tags,
          availableTime,
          bookingLink,
          timezone,
          countryCode,
          uid,
        } = profile!;
        setValue('name', name);
        setValue('pictureUrl', pictureUrl);
        setValue('profileBio', profileBio);
        setValue('email', email);
        setValue('title', title);
        setValue('tags', tags);
        
        // Handle both old string format and new WeeklySchedule format
        if (typeof availableTime === 'string') {
          // If it's an old string format, use defaultWeeklySchedule for now
          // In the future, you could add parsing logic to convert string to schedule
          setValue('availableTime', defaultWeeklySchedule);
        } else {
          // It's already in WeeklySchedule format
          setValue('availableTime', availableTime || defaultWeeklySchedule);
        }
        
        setValue('bookingLink', bookingLink);
        setValue('timezone', timezone || dayjs.tz.guess()); // Fallback to detected timezone
        setValue('countryCode', countryCode);
        setValue('uid', uid);
        setUserDocID(uid);
      };

      fetchData();
    }
  }, [user, setValue, navigate]);
  if (!isMentor) {
    navigate('/door/signup');
    return null;
  }
  const onSubmit: SubmitHandler<any> = async (data) => {
    data.tags = Array.isArray(data.tags) ? data.tags : data.tags.split(',').map((tag:string) => tag.trim());
    const mentorData: Mentor = data as Mentor;
    mentorData.name = mentorData.name.trim();
    mentorData.title = mentorData.title!.trim();
    mentorData.email = mentorData.email.trim();
    await updateMentor(userDocID, mentorData);
    setSnackBarOpen(true);
  };

  const handleActivate = () => {
    if (mentorActive === false) {
      activateMentor(userDocID);
      setMentorActive(true);
    } else {
      setMsgBoxOpen(true);
    }
  };

  const handleConfirm = (confirm?: boolean) => {
    if (confirm) {
      if (mentorActive) {
        deactivateMentor(userDocID);
        navigate('/door');
      } else {
        activateMentor(userDocID);
        setMentorActive(true);
      }
    }
    setMsgBoxOpen(false);
  };
  const backToDoor = () => {
    navigate('/door');
    return null;
  };
  return (
    <Box my={5}>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={snackBarOpen}
        message="成功更新Mentor資料"
        action={
          <Button color="secondary" onClick={backToDoor}>
            回首頁
          </Button>
        }
      />
      <Container maxWidth="md" sx={{ my: 4 }}>
        <MentorForm
          control={control}
          errors={errors}
          handleSubmit={handleSubmit(onSubmit)}
          isSubmitting={isSubmitting}
          submitted={snackBarOpen}
          additionalButton={
            <Button 
              startIcon={<PauseIcon />}
              onClick={handleActivate} 
              sx={{ height: '40px' }} 
              color="secondary"
            >
              {mentorActive ? '暫停導師身份' : '重新啟用導師身份'}
            </Button>
          }
        />
        <ConfirmModal
          title={mentorActive ? '確認' : '提醒'}
          name={
            mentorActive
              ? '確定要辭去導師身分嗎?'
              : '您已經暫停導師身分，是否要重新啟用導師身分'
          }
          open={msgBoxOpen}
          onClose={() => setMsgBoxOpen(false)}
          onProceed={handleConfirm}
        />
      </Container>
    </Box>
  );
};
